import { useCallback, useEffect, useState } from "react";

export type ListSubFormState<T> = {
  addItem: (item: T) => void;
  setItem: (item: T, idx: number) => void;
  delItem: (idx: number) => void;
  moveItem: (idx: number, newIdx: number) => void;
  items: T[];
};

export default function useListSubFormState<ItemType>(
  initialData?: ItemType[]
): ListSubFormState<ItemType> {
  const [items, setItems] = useState<ItemType[]>(initialData ?? []);

  useEffect(() => {
    setItems(initialData ?? []);
  }, [initialData]);

  const addItem = (item: ItemType) => {
    setItems(f => [...f, item]);
  };
  const setItem = (item: ItemType, index: number) => {
    const updatedData = items.map((d, idx) => {
      return idx === index ? item : d;
    });
    setItems(updatedData);
  };
  const delItem = (index: number) => {
    setItems(items.filter((_, idx) => idx !== index));
  };
  const moveItem = useCallback(
    (index: number, newIndex: number) => {
      const invalid =
        index < 0 ||
        index >= items.length ||
        newIndex < 0 ||
        newIndex >= items.length ||
        index === newIndex;
      if (invalid) return;

      const item = items[index];
      if (index > newIndex) {
        const bfs = items.filter((_, idx) => idx !== index && idx < newIndex);
        const afs = items.filter((_, idx) => idx !== index && idx >= newIndex);
        setItems([...bfs, item, ...afs]);
      } else {
        const bfs = items.filter((_, idx) => idx !== index && idx <= newIndex);
        const afs = items.filter((_, idx) => idx !== index && idx > newIndex);
        setItems([...bfs, item, ...afs]);
      }
    },
    [items]
  );

  return {
    addItem,
    setItem,
    delItem,
    moveItem,
    items
  };
}
